import moment from 'moment'
import { getSessionId, Utils } from '@pbt/pbt-ui-components'
import {
  Environment,
  Region,
} from '@pbt/pbt-ui-components/src/constants/environment'
import { getDomainContext } from '@pbt/pbt-ui-components/src/utils'

import { tokenHolder } from './utils/token'

export interface SSEOptions {
  onClose?: () => void
  onError?: (error: Event) => void
  onMessage?: (data: SSEMessage) => void
  onOpen?: () => void
}

export interface SSEContentPart {
  functionCall?: {
    args: Record<string, any>
    id: string
    name: string
  }
  functionResponse?: {
    id: string
    name: string
    response: any
  }
  text?: string
}

export interface SSEContent {
  invocationId?: string
  parts: SSEContentPart[]
}

export interface SSEActions {
  artifactDelta?: Record<string, any>
  stateDelta?: Record<string, any>
}

export interface SSEMessage {
  actions?: SSEActions
  agent?: string
  author?: string
  content?: SSEContent
  finalReportWithCitations?: boolean
  id?: string
  invocationId?: string
  partial?: boolean
  processedEvents?: ProcessedEvent[]
  timestamp?: number
  usageMetadata?: {
    candidatesTokenCount?: number
    promptTokenCount?: number
    totalTokenCount?: number
  }
}

export interface ProcessedEvent {
  data: Record<string, any>
  title: string
}

export const SSEMessageUtils = {
  extractTextContent: (message: SSEMessage): string => {
    if (!message.content?.parts) {
      return ''
    }

    return message.content.parts
      .filter((part) => part.text)
      .map((part) => part.text!)
      .join(' ')
  },

  hasTextContent: (message: SSEMessage): boolean =>
    SSEMessageUtils.extractTextContent(message).trim().length > 0,

  hasStateUpdates: (message: SSEMessage): boolean =>
    Boolean(message.actions?.stateDelta),

  getTimestamp: (message: SSEMessage): string =>
    message.timestamp
      ? moment(message.timestamp * 1000).toISOString()
      : moment().toISOString(),
}

export interface SSERequestConfig {
  auth?: boolean
  data?: any
  headers?: Record<string, string>
  method?: 'GET' | 'POST'
  signal?: AbortSignal
  url: string
}

export interface SSERetryOptions {
  baseDelay?: number
  maxDelay?: number
  maxDuration?: number
  maxRetries?: number
}

export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: SSERetryOptions = {},
): Promise<T> => {
  const {
    maxRetries = 3,
    maxDuration = 30000, // 30 seconds
    baseDelay = 1000,
    maxDelay = 5000,
  } = options

  const startTime = Date.now()
  let lastError: Error = new Error('No error thrown, but retrying failed')

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (Date.now() - startTime > maxDuration) {
      throw new Error(`Retry timeout after ${maxDuration}ms`)
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn()
    } catch (error) {
      lastError = error as Error

      // Don't retry on the last attempt
      if (attempt === maxRetries - 1) {
        break
      }

      const delay = Math.min(baseDelay * 2 ** attempt, maxDelay)

      // eslint-disable-next-line no-console
      console.log(
        `SSE retry attempt ${attempt + 1}/${maxRetries} failed, retrying in ${delay}ms...`,
        error,
      )

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, delay)
      })
    }
  }

  throw lastError!
}

const getSSEBaseUrl = (): string => {
  const domainContext = getDomainContext()

  const sseUrlMap: Record<Region, Partial<Record<Environment, string>>> = {
    [Region.US]: {
      [Environment.PROD]: 'https://api.rhapsody.vet',
      [Environment.MASTER]: 'https://api.master.rhapsody.vet',
      [Environment.STAGE]: 'https://api.stage.rhapsody.vet',
    },
    [Region.EU]: {
      [Environment.PROD]: 'https://api.eu.rhapsody.vet',
      [Environment.MASTER]: 'https://api.eu.master.rhapsody.vet',
      [Environment.STAGE]: 'https://api.eu.stage.rhapsody.vet',
    },
  }

  const envApiMap = sseUrlMap[domainContext.region]
  const defaultApiUrl = envApiMap?.[Environment.STAGE]

  return envApiMap?.[domainContext.env] || defaultApiUrl || ''
}

const getSSERequestHeaders = async (
  auth: boolean = true,
): Promise<Record<string, string>> => {
  let headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
  }

  if (auth) {
    const token = await tokenHolder.getToken()
    headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
      'X-Business-Id': localStorage.getItem('businessId') || '',
      'X-PBT-SessionId': getSessionId(),
    }
  }

  if (Utils.isLocalEnvironment()) {
    headers = {
      ...headers,
      'X-PBT-DevEnv': window.pbt.getDevEnv(),
      'X-PBT-Region': window.pbt.getDevRegion(),
    }
  }

  return headers
}

export const createSSEConnection = async (
  config: SSERequestConfig,
  options: SSEOptions = {},
  retryOptions: SSERetryOptions = {},
): Promise<{
  reader: ReadableStreamDefaultReader<Uint8Array> | null
  stream: ReadableStream<Uint8Array> | null
}> => {
  const {
    url,
    method = 'GET',
    data,
    headers = {},
    auth = true,
    signal,
  } = config
  const { onMessage, onError, onOpen, onClose } = options

  return retryWithBackoff(async () => {
    const baseUrl = getSSEBaseUrl()
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

    const requestHeaders = await getSSERequestHeaders(auth)
    const finalHeaders = { ...requestHeaders, ...headers }

    const response = await fetch(fullUrl, {
      method,
      headers: finalHeaders,
      body: method === 'POST' && data ? JSON.stringify(data) : undefined,
      signal, // Add AbortController support
    })

    if (!response.ok) {
      throw new Error(
        `SSE request failed: ${response.status} ${response.statusText}`,
      )
    }

    if (onOpen) {
      onOpen()
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Response body is not readable')
    }

    const processStream = async () => {
      try {
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read()

          if (value) {
            buffer += decoder.decode(value, { stream: true })
          }

          // Process complete lines
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          // eslint-disable-next-line no-restricted-syntax
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataLine = line.slice(6) // Remove 'data: ' prefix
              if (dataLine.trim() && onMessage) {
                try {
                  const parsedData = JSON.parse(dataLine) as SSEMessage
                  onMessage(parsedData)
                } catch (error) {
                  // If JSON parsing fails, create a basic message with the raw data
                  const fallbackMessage: SSEMessage = {
                    content: {
                      parts: [{ text: dataLine }],
                    },
                    timestamp: Date.now() / 1000,
                  }
                  onMessage(fallbackMessage)
                }
              }
            }
          }

          if (done) {
            if (onClose) {
              onClose()
            }
            break
          }
        }
      } catch (error) {
        if (onError) {
          onError(error as Event)
        }
      }
    }

    processStream()
    return {
      stream: response.body,
      reader,
    }
  }, retryOptions)
}
